import React from "react";
import { Link } from "react-router-dom";
import Nav from "../site/Nav";
import SEO from "../site/SEO";
import Footer from "../site/Footer";
import ReadingProgress from "../site/ReadingProgress";
import useScrollReveal from "../../hooks/useScrollReveal";

export default function PrivacyPage() {
  useScrollReveal();
  return (
    <div className="bg-sand-100 text-ink min-h-screen" data-testid="privacy-page">
      <SEO page="/privacy" />
      <ReadingProgress />
      <Nav />
      <main className="pt-24 sm:pt-28 md:pt-32 pb-20">
        <article className="max-w-3xl mx-auto px-5 sm:px-6 md:px-10">
          <header className="reveal">
            <p className="eyebrow !text-clay">Legal</p>
            <h1 data-kinetic className="font-display mt-5 text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.02em] font-semibold text-ink">
              Privacy Policy
            </h1>
            <p className="mt-4 font-mono text-[10.5px] uppercase tracking-[0.18em] text-ink-muted">
              Last updated · February 2026
            </p>
          </header>

          <div className="mt-12 reveal prose prose-sm max-w-none text-ink
              prose-headings:font-display prose-headings:tracking-tight prose-headings:text-ink
              prose-h2:text-[1.4rem] prose-h2:mt-10 prose-h2:mb-4
              prose-h3:text-[1.1rem] prose-h3:mt-6 prose-h3:mb-2
              prose-p:text-[15px] prose-p:leading-relaxed prose-p:text-ink-muted
              prose-li:text-[15px] prose-li:text-ink-muted
              prose-strong:text-ink prose-a:text-clay hover:prose-a:text-rust">

            <p>
              The Modern Exit (&ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;) respects your
              privacy. This Privacy Policy explains how we collect, use, and
              safeguard information when you visit{" "}
              <a href="https://themodernexit.net">themodernexit.net</a> or
              engage us for website or revenue-system services.
            </p>

            <h2>Information we collect</h2>
            <p>We collect information you provide directly, including:</p>
            <ul>
              <li><strong>Contact details</strong> — name, business name, email, phone number.</li>
              <li><strong>Project intake information</strong> — answers submitted through our Website Build Brief, audit form, or other intake flows.</li>
              <li><strong>Payment information</strong> — processed securely by Stripe. We never see or store full card numbers on our servers.</li>
              <li><strong>Technical data</strong> — IP address, browser type, referrer, UTM campaign parameters, and pages visited.</li>
            </ul>

            <h2>How we use your information</h2>
            <ul>
              <li>To deliver the services you request and respond to your inquiries.</li>
              <li>To process payments and send receipts, project updates, and kickoff documents.</li>
              <li>To improve our site, services, and intake flows.</li>
              <li>To comply with legal obligations and enforce our Terms of Service.</li>
            </ul>

            <h2>Cookies &amp; analytics</h2>
            <p>
              We use cookies and similar technologies to remember your
              preferences, understand how visitors use the site, and measure
              marketing performance. You can disable cookies in your browser
              settings, but some parts of the site may not function correctly.
            </p>

            <h2>Third-party services</h2>
            <p>We rely on a small set of vetted providers to deliver our services:</p>
            <ul>
              <li><strong>Stripe</strong> — payment processing</li>
              <li><strong>Resend</strong> — transactional email delivery</li>
              <li><strong>GoHighLevel</strong> — CRM and pipeline management</li>
              <li><strong>MongoDB Atlas</strong> — encrypted database storage</li>
              <li><strong>Google Analytics / Search Console</strong> — site analytics and search performance</li>
            </ul>
            <p>
              These providers process information strictly on our behalf under
              their own privacy commitments.
            </p>

            <h2>How we protect your information</h2>
            <p>
              We use industry-standard safeguards including HTTPS-only
              transport, encrypted database storage, and access controls. No
              method of online transmission is 100% secure, but we never sell
              or rent your personal information.
            </p>

            <h2>Your rights</h2>
            <p>
              You may request access to, correction of, or deletion of your
              personal information at any time by emailing{" "}
              <a href="mailto:info@themodernexit.net">info@themodernexit.net</a>.
              We respond within 30 days. Residents of California, Colorado,
              Virginia, and other states with privacy laws may have additional
              rights under applicable statutes.
            </p>

            <h2>Children&rsquo;s privacy</h2>
            <p>
              Our services are not directed to individuals under 16. We do not
              knowingly collect personal information from children.
            </p>

            <h2>Changes to this policy</h2>
            <p>
              We may update this policy periodically. Material changes will be
              reflected in the &ldquo;Last updated&rdquo; date above.
            </p>

            <h2>Contact</h2>
            <p>
              Questions about this Privacy Policy? Email{" "}
              <a href="mailto:info@themodernexit.net">info@themodernexit.net</a>{" "}
              or call{" "}
              <a href="tel:+12142539779">+1 (214) 253-9779</a>.
            </p>
          </div>

          <div className="mt-14 reveal">
            <Link
              to="/"
              data-testid="privacy-back-home"
              className="inline-flex items-center gap-2 font-mono text-[10.5px] uppercase tracking-[0.18em] text-clay hover:text-rust"
            >
              ← Back to home
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
